import React, { useEffect, useState, useCallback } from "react";
import {
  Users as UsersIcon, RefreshCw, Search, ShieldCheck, BadgeCheck,
  Crown, Clock3, Mail, Calendar, Download, Ban, Undo2, Activity,
  MailCheck, AlertCircle, CheckCircle2, Copy,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const SORTS = [
  { key: "created_at", label: "Newest signup" },
  { key: "last_seen_at", label: "Last seen" },
  { key: "last_login_at", label: "Last sign-in" },
  { key: "login_count", label: "Most logins" },
];

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMin = Math.floor((now - d) / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h ago`;
    if (diffMin < 60 * 24 * 7) return `${Math.floor(diffMin / 1440)}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso?.slice(0, 10) || "—"; }
};

const Stat = ({ label, value, accent }) => (
  <div className="rounded-2xl bg-white/[0.03] border border-white/8 p-4 flex-1 min-w-[120px]">
    <div className={`text-2xl font-display font-semibold ${accent ? "text-[#E2FF31]" : "text-white"}`}>{value}</div>
    <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 font-semibold mt-1">{label}</div>
  </div>
);

// ---------- Email sender (Resend) domain status panel ----------
const EmailDomainPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/digest/admin/domain-status");
      setData(r.data);
    } catch (e) {
      setData({ error: e?.response?.data?.detail || "Failed to load domain status" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const verify = async (domainId) => {
    setVerifying(domainId);
    try {
      await api.post(`/digest/admin/domain-verify/${domainId}`);
      toast.success("Verification triggered — refreshing…");
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally {
      setVerifying(null);
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copied");
  };

  if (!data) return null;
  if (data.error) {
    return (
      <div className="rounded-2xl bg-white/[0.03] border border-white/8 p-4 mb-6 text-sm text-white/55">
        <p className="flex items-center gap-2"><AlertCircle size={14} className="text-[#FF2E00]" /> {data.error}</p>
      </div>
    );
  }

  return (
    <details
      data-testid="admin-email-domain-panel"
      className="rounded-2xl bg-white/[0.03] border border-white/8 mb-6 group overflow-hidden"
    >
      <summary className="px-4 py-3 cursor-pointer flex items-center justify-between gap-3 list-none">
        <div className="flex items-center gap-2 min-w-0">
          <MailCheck size={15} className="text-[#E2FF31] shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold">Email sending</p>
            <p className="text-[11px] text-white/45 truncate">
              From <span className="text-white/70 font-mono">{data.sender_email}</span>
              {data.domains.length > 0 && (
                <>
                  {" · "}
                  {data.domains.map((d) => `${d.name}: ${d.status}`).join(" · ")}
                </>
              )}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); load(); }}
          className="p-1.5 rounded-full hover:bg-white/10 transition shrink-0"
          data-testid="admin-domain-refresh"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
        </button>
      </summary>
      <div className="px-4 pb-4">
        {data.domains.length === 0 ? (
          <p className="text-xs text-white/45 mt-1">No custom domains configured. Add one in Resend dashboard.</p>
        ) : (
          data.domains.map((d) => {
            const verified = d.status === "verified";
            return (
              <div
                key={d.id}
                data-testid={`admin-domain-${d.id}`}
                className="mt-2 rounded-xl border border-white/8 bg-black/30 p-3"
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-mono text-sm truncate">{d.name}</span>
                    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold flex items-center gap-1 ${
                      verified
                        ? "bg-emerald-500/15 text-emerald-300"
                        : "bg-yellow-400/15 text-yellow-300"
                    }`}>
                      {verified ? <CheckCircle2 size={9} /> : <AlertCircle size={9} />}
                      {d.status?.replace(/_/g, " ")}
                    </span>
                  </div>
                  {!verified && (
                    <button
                      type="button"
                      onClick={() => verify(d.id)}
                      disabled={verifying === d.id}
                      data-testid={`admin-domain-verify-${d.id}`}
                      className="text-[11px] font-semibold rounded-full px-3 py-1.5 bg-white text-black hover:bg-white/90 disabled:opacity-50 transition"
                    >
                      {verifying === d.id ? "Verifying…" : "Verify now"}
                    </button>
                  )}
                </div>
                {!verified && d.records?.length > 0 && (
                  <div className="mt-3 text-[11px] text-white/55">
                    <p className="mb-1.5 text-white/70 font-semibold">
                      Add these records on your DNS provider (Porkbun → Manage DNS):
                    </p>
                    <div className="space-y-2">
                      {d.records.map((r, i) => (
                        <div
                          key={`${r.type}-${r.name}-${i}`}
                          className="rounded-lg border border-white/8 bg-black/40 p-2 font-mono break-all"
                        >
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="text-[9px] uppercase tracking-wider bg-white/10 px-1.5 py-0.5 rounded font-bold not-italic">{r.type}</span>
                            <span className="text-white/70 text-xs">{r.name}</span>
                            {r.priority != null && (
                              <span className="text-[10px] text-white/50">priority {r.priority}</span>
                            )}
                          </div>
                          <div className="text-[10px] text-white/45 flex items-start gap-2">
                            <span className="flex-1 break-all">{r.value}</span>
                            <button
                              type="button"
                              onClick={() => copy(r.value)}
                              className="p-1 rounded hover:bg-white/10 transition shrink-0"
                              title="Copy value"
                            >
                              <Copy size={11} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="mt-2 text-[10px] text-white/40">
                      After adding, click <span className="text-white">Verify now</span>. DNS can take up to 48 hours.
                    </p>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </details>
  );
};

// ---------- Activity chart (signups + sign-ins, last 14 days) ----------
const ActivityChart = ({ data }) => {
  if (!data || data.length === 0) return null;
  const maxV = Math.max(1, ...data.map((d) => Math.max(d.signups, d.signins)));
  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/8 p-4 sm:p-5 mb-6">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Activity size={14} className="text-[#E2FF31]" />
          <p className="text-[11px] uppercase tracking-[0.2em] text-white/60 font-semibold">Last 14 days</p>
        </div>
        <div className="flex items-center gap-4 text-[11px]">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[#E2FF31]"></span> Signups</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[#FF2E00]"></span> Sign-ins</span>
        </div>
      </div>
      <div className="flex items-end justify-between gap-1 sm:gap-2 h-32">
        {data.map((d, i) => {
          const hSignup = Math.round((d.signups / maxV) * 100);
          const hSignin = Math.round((d.signins / maxV) * 100);
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-0.5 min-w-0" title={`${d.date} · ${d.signups} signups · ${d.signins} sign-ins`}>
              <div className="w-full flex items-end justify-center gap-0.5 h-24">
                <div
                  className="w-1/2 bg-[#E2FF31] rounded-sm transition-all"
                  style={{ height: `${Math.max(2, hSignup)}%`, opacity: d.signups ? 1 : 0.15 }}
                />
                <div
                  className="w-1/2 bg-[#FF2E00] rounded-sm transition-all"
                  style={{ height: `${Math.max(2, hSignin)}%`, opacity: d.signins ? 1 : 0.15 }}
                />
              </div>
              <span className="text-[9px] text-white/40 font-medium">{d.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------- Per-row block / unblock controls ----------
const BlockButton = ({ user, onChanged }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const block = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.user_id}/block`, { reason });
      toast.success(`Blocked ${user.email}`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Block failed");
    } finally {
      setBusy(false);
      setReason("");
    }
  };

  const unblock = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.user_id}/unblock`);
      toast.success(`Unblocked ${user.email}`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unblock failed");
    } finally {
      setBusy(false);
    }
  };

  if (user.blocked) {
    return (
      <button
        type="button"
        data-testid={`admin-unblock-${user.user_id}`}
        onClick={unblock}
        disabled={busy}
        className="text-xs flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/5 hover:bg-white/15 text-white/70 hover:text-white transition disabled:opacity-50"
      >
        <Undo2 size={11} /> Unblock
      </button>
    );
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button
          type="button"
          data-testid={`admin-block-${user.user_id}`}
          disabled={busy}
          className="text-xs flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/5 hover:bg-[#FF2E00]/15 text-white/55 hover:text-[#FF2E00] transition disabled:opacity-50"
        >
          <Ban size={11} /> Block
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[#0a0a0a] border-white/10 text-white">
        <AlertDialogHeader>
          <AlertDialogTitle>Block {user.name || user.email}?</AlertDialogTitle>
          <AlertDialogDescription className="text-white/60">
            They will be signed out immediately and unable to sign back in.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, 200))}
          rows={2}
          placeholder="Reason (optional, for your records)"
          className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-white/30 transition resize-none my-2"
        />
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-white/5 hover:bg-white/15 border-white/10 text-white">Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={block}
            data-testid={`admin-confirm-block-${user.user_id}`}
            className="bg-[#FF2E00] hover:bg-[#E62900] text-white"
          >
            Block user
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const AdminUsers = () => {
  const { user, login } = useAuth();
  const [authChecked, setAuthChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("created_at");
  const [q, setQ] = useState("");
  const [activityFilter, setActivityFilter] = useState("all"); // all | active | inactive

  useEffect(() => {
    if (!user) { setAuthChecked(true); return; }
    api.get("/admin/whoami")
      .then((r) => setIsAdmin(!!r.data?.is_admin))
      .catch(() => setIsAdmin(false))
      .finally(() => setAuthChecked(true));
  }, [user]);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const [usersRes, actRes] = await Promise.all([
        api.get("/admin/users", { params: {
          sort,
          limit: 500,
          q: q.trim() || undefined,
          activity: activityFilter === "all" ? undefined : activityFilter,
        }}),
        api.get("/admin/users/activity", { params: { days: 14 } }),
      ]);
      setRows(usersRes.data?.results || []);
      setStats(usersRes.data?.stats || {});
      setActivity(actRes.data?.results || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, sort, q, activityFilter]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/export.csv`;
    // Cookie-based auth works; open in same tab for direct download
    window.open(url, "_blank");
  };

  if (!user) return (
    <div data-testid="admin-users-page" className="py-24 text-center px-4">
      <UsersIcon size={36} className="mx-auto text-white/30 mb-4" />
      <h2 className="font-display text-3xl mb-2">Admin sign-in required</h2>
      <button onClick={login} data-testid="admin-users-signin" className="mt-4 bg-white text-black rounded-full px-6 py-2.5 font-semibold">Sign in</button>
    </div>
  );
  if (!authChecked) return <div className="py-16 text-center text-white/40">Checking access…</div>;
  if (!isAdmin) return (
    <div data-testid="admin-users-page" className="py-24 text-center px-4">
      <ShieldCheck size={36} className="mx-auto text-[#FF2E00]/70 mb-4" />
      <h2 className="font-display text-3xl mb-2">Admin access required</h2>
    </div>
  );

  return (
    <div data-testid="admin-users-page" className="pb-24 max-w-5xl mx-auto px-1 sm:px-0">
      <div className="mb-6">
        <p className="text-[11px] uppercase tracking-[0.3em] text-white/50 mb-2 flex items-center gap-2">
          <ShieldCheck size={12} className="text-[#E2FF31]" /> Admin Dashboard
        </p>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tighter">All Users</h1>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-users-export"
              onClick={downloadCsv}
              className="flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-2 bg-white/5 hover:bg-white/15 transition"
              title="Download as CSV"
            >
              <Download size={13} /> CSV
            </button>
            <button
              data-testid="admin-users-refresh"
              onClick={load}
              className="p-2.5 rounded-full bg-white/5 hover:bg-white/15 active:scale-95 transition"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
      </div>

      {/* Email sender domain status (Resend) */}
      <EmailDomainPanel />

      {/* Stat chips */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <Stat label="Total users" value={stats.total ?? "—"} />
        <Stat label="Artists" value={stats.artists ?? "—"} />
        <Stat label="Premium" value={stats.premium ?? "—"} accent />
        <Stat label="New this week" value={stats.new_this_week ?? "—"} />
        <Stat label="Active in 24h" value={stats.active_24h ?? "—"} accent />
        <Stat label="Inactive 7d+" value={stats.inactive ?? "—"} />
      </div>

      {/* Activity chart */}
      <ActivityChart data={activity} />

      {/* Activity filter chips */}
      <div className="flex gap-2 mb-4 overflow-x-auto hide-scrollbar -mx-1 px-1 pb-1">
        {[
          { key: "all",      label: "All",           count: stats.total },
          { key: "active",   label: "Active 7d",     count: stats.active_7d },
          { key: "inactive", label: "Inactive 7d+",  count: stats.inactive },
        ].map((f) => (
          <button
            key={f.key}
            data-testid={`admin-users-filter-${f.key}`}
            onClick={() => setActivityFilter(f.key)}
            className={`px-3.5 py-2 rounded-full text-xs font-medium whitespace-nowrap border transition flex items-center gap-1.5 ${
              activityFilter === f.key
                ? "bg-white text-black border-white"
                : "bg-white/[0.04] text-white/70 border-white/10 hover:bg-white/[0.08]"
            }`}
          >
            {f.label}
            {f.count != null && (
              <span
                className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  activityFilter === f.key ? "bg-black/15 text-black" : "bg-white/10 text-white/60"
                }`}
              >
                {f.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Sort + search */}
      <div className="flex flex-col sm:flex-row gap-2 mb-5">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="admin-users-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by email, name, or artist name…"
            className="w-full bg-white/[0.03] border border-white/8 rounded-full pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-white/25 transition"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto hide-scrollbar -mx-1 px-1 pb-1">
          {SORTS.map((s) => (
            <button
              key={s.key}
              data-testid={`admin-users-sort-${s.key}`}
              onClick={() => setSort(s.key)}
              className={`px-3.5 py-2 rounded-full text-xs font-medium whitespace-nowrap border transition ${
                sort === s.key
                  ? "bg-white text-black border-white"
                  : "bg-white/[0.04] text-white/70 border-white/10 hover:bg-white/[0.08]"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <p className="text-white/40 py-12 text-center">Loading users…</p>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center text-white/45">No users match.</div>
      ) : (
        <div className="rounded-2xl bg-white/[0.03] border border-white/8 overflow-hidden">
          {rows.map((u) => (
            <div
              key={u.user_id}
              data-testid={`admin-user-row-${u.user_id}`}
              className={`flex items-center gap-3 px-3 sm:px-4 py-3 border-b border-white/5 last:border-b-0 hover:bg-white/[0.03] transition ${
                u.blocked ? "opacity-60" : ""
              }`}
            >
              {u.picture ? (
                <img src={u.picture} alt="" className="w-10 h-10 rounded-full object-cover shrink-0 ring-1 ring-white/10" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF2E00]/40 to-[#FF2E00]/30 flex items-center justify-center shrink-0 text-sm font-semibold">
                  {(u.name || u.email || "?")[0]?.toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <p className="text-sm font-semibold truncate">{u.name || u.email}</p>
                  {u.is_artist && (
                    <span className="flex items-center gap-1 text-[9px] uppercase tracking-wider bg-[#E2FF31]/15 text-[#E2FF31] px-1.5 py-0.5 rounded font-bold">
                      <BadgeCheck size={9} /> Artist
                    </span>
                  )}
                  {u.premium_tier && u.premium_tier !== "free" && (
                    <span className="flex items-center gap-1 text-[9px] uppercase tracking-wider bg-[#FF2E00]/15 text-[#FF2E00] px-1.5 py-0.5 rounded font-bold capitalize">
                      <Crown size={9} /> {u.premium_tier}
                    </span>
                  )}
                  {u.blocked && (
                    <span className="flex items-center gap-1 text-[9px] uppercase tracking-wider bg-red-500/20 text-red-300 px-1.5 py-0.5 rounded font-bold">
                      <Ban size={9} /> Blocked
                    </span>
                  )}
                </div>
                <div className="text-xs text-white/50 flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 truncate">
                  <span className="flex items-center gap-1 truncate"><Mail size={10} /> {u.email}</span>
                </div>
                <div className="text-[11px] text-white/35 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                  <span className="flex items-center gap-1"><Calendar size={10} /> joined {fmt(u.created_at)}</span>
                  <span className="flex items-center gap-1"><Clock3 size={10} /> seen {fmt(u.last_seen_at || u.last_login_at)}</span>
                  {u.login_count ? <span>· {u.login_count} sign-in{u.login_count === 1 ? "" : "s"}</span> : null}
                </div>
              </div>
              <BlockButton user={u} onChanged={load} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
